import { useMemo, useSyncExternalStore, type ReactNode } from "react";
import type { TrainingGpuClusterUsage, TrainingGpuWorkspaceUsage } from "@/api/trainingGpu";
import type { LoadState } from "@/components/dataset/loadState";
import { gpuCapacityController } from "@/components/gpu/gpuCapacityController";
import { ChartCard, StatTile } from "@/components/metric";
import { formatCompactCount } from "@/utils/formats";
import { UsageTrendChart } from "./charts";
import {
  buildGpuUsageStripTiles,
  describeUsageTrend,
  toUsageTrendPoints,
} from "./gpuUsageStripModel";
import styles from "./GpuUsageStrip.module.css";

// GPU 사용량 요약 띠. 타일 다섯 장과 일별 보유·사용 차트를 그린다.
//
// 세 출처를 쓴다. workspace 사용량과 클러스터 점유는 띠를 둔 화면이 읽어서 전달한다. 가용량은
// 사이드바 위젯이 15초마다 받는 스냅샷을 그대로 읽는다. 그래서 이 띠는 요청을 보내지 않는다.
// 한 출처가 실패해도 다른 출처의 타일은 그린다.
export function GpuUsageStrip({
  usage,
  cluster,
  onRetry,
}: {
  // 기간 내 workspace 사용량. 응답이 없으면 null 이다. 조회 전과 조회 실패가 여기에 든다.
  usage: TrainingGpuWorkspaceUsage | null;
  cluster: LoadState<TrainingGpuClusterUsage>;
  // 클러스터 추세를 다시 받는다. 추세 카드의 조회 실패가 쓴다.
  onRetry: () => void;
}): ReactNode {
  const capacity = useSyncExternalStore(
    gpuCapacityController.subscribe,
    gpuCapacityController.getSnapshot,
  ).data;
  const clusterData = cluster.status === "success" ? cluster.data : null;

  const tiles = useMemo(
    () => buildGpuUsageStripTiles({ usage, cluster: clusterData, capacity }),
    [usage, clusterData, capacity],
  );
  const points = useMemo(
    () => (clusterData === null ? [] : toUsageTrendPoints(clusterData)),
    [clusterData],
  );

  return (
    <section className={styles.strip} aria-label="GPU 사용량 요약">
      <div className={styles.tiles}>
        {tiles.map(({ key, ...tile }) => (
          // 다섯 타일이 모두 보조 문구를 가진다. 그래서 문구를 값 아래에 둔다(StatTile 규약).
          <StatTile key={key} {...tile} hintPlacement="below" />
        ))}
      </div>
      <ChartCard
        title="일별 GPU-hours"
        // 이 차트는 5분 표본을 적분한 값이다. 타일의 기간 GPU-hours(attempt 기준)와 출처가 다르다.
        subtitle="클러스터 보유와 사용 · 표본 기준 · UTC 일"
        loading={
          cluster.status === "idle" || cluster.status === "loading"
            ? "클러스터 점유 추세를 불러오고 있습니다."
            : undefined
        }
        error={cluster.status === "error" ? { message: cluster.message, onRetry } : undefined}
        empty={
          cluster.status === "success" && (points.length === 0 || cluster.data.days.length === 0)
            ? "이 기간에는 점유 표본이 없습니다."
            : undefined
        }
      >
        {clusterData === null ? null : (
          <UsageTrendChart
            points={points}
            heldLabel="보유"
            usedLabel="사용"
            formatValue={formatCompactCount}
            ariaLabel={describeUsageTrend(clusterData)}
          />
        )}
      </ChartCard>
    </section>
  );
}
